import React, { useState, useRef } from 'react';
import { confirmDialog } from '../../utils/notifications.ts';
import { uploadOrgLogo } from '../../features/files/utils/storage-service.ts';
import { showToast } from '../../utils/notifications.ts';
import { logger } from '../../utils/logger.ts';
import { Icon } from '../../components/common/Icon.tsx';
import { Button } from './Button.tsx';

const NativeInput = 'input';

interface LogoUploaderProps {
    /** Current logo URL (Firebase Storage URL or legacy data URL) */
    currentLogo: string | null | undefined;
    /** Callback when logo changes */
    onLogoChange: (url: string | null) => void;
    /** Optional placeholder text */
    placeholder?: string;
    /** Optional CSS class */
    className?: string;
    /** If true, displays logo without edit functionality */
    readOnly?: boolean;
    /** Uploads directly to organizations/{orgId}/logo/ using a native file dialog. */
    orgId?: string;
}

/**
 * LogoUploader — logo selection and upload component.
 *
 * Org mode (orgId provided):
 *   Uses a hidden native <input type="file"> and uploads directly to
 *   organizations/{orgId}/logo/ in Firebase Storage. No project required.
 * Project production logos use ProjectMediaUploader instead of repository files.
 */
export const LogoUploader: React.FC<LogoUploaderProps> = ({
    currentLogo,
    onLogoChange,
    placeholder = 'Click to select image',
    className = '',
    readOnly = false,
    orgId,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Org mode handlers ────────────────────────────────────────────────────

    const handleOrgClick = async () => {
        if (readOnly || isUploading) return;
        if (currentLogo) {
            if (await confirmDialog('Remove this logo?')) {
                onLogoChange(null);
            }
        } else {
            fileInputRef.current?.click();
        }
    };

    const handleOrgFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !orgId) return;
        // Reset so the same file can be picked again if needed
        e.target.value = '';

        setIsUploading(true);
        setUploadProgress(0);
        try {
            const result = await uploadOrgLogo(file, orgId, (progress) => {
                setUploadProgress(Math.round(progress.percent));
            });
            if (!result.url) {
                throw new Error('Logo upload did not return a download URL');
            }
            onLogoChange(result.url);
            showToast('Logo uploaded successfully', 'success');
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Upload failed';
            logger.error('Org logo upload error:', error);
            showToast(msg, 'error');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // ── Read-only display ─────────────────────────────────────────────────────

    if (readOnly) {
        return (
            <div className={`relative ${className}`}>
                {currentLogo ? <img
                        src={currentLogo}
                        alt="Logo"
                        width={200}
                        height={100}
                        className="max-h-[100px] max-w-[200px] object-contain"
                    /> : null}
            </div>
        );
    }

    // ── Shared inner visual ───────────────────────────────────────────────────

    const logoContent = currentLogo ? (
        <div className="relative">
            <img
                src={currentLogo}
                alt="Logo"
                width={200}
                height={100}
                className="max-h-[100px] max-w-[200px] object-contain"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <Icon name="close" size="sm" className="text-white" />
            </div>
        </div>
    ) : (
        <div className="flex h-[100px] w-[200px] items-center justify-center rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-primary hover:bg-primary/5 dark:border-gray-600">
            <div className="flex flex-col items-center text-gray-400 dark:text-gray-500">
                {isUploading ? (
                    <>
                        <Icon name="progress_activity" size="lg" className="animate-spin" />
                        <span className="mt-1 text-center text-sm leading-tight">{uploadProgress}%</span>
                    </>
                ) : (
                    <>
                        <Icon name="add_photo_alternate" size="lg" />
                        <span className="mt-1 text-center text-sm leading-tight">{placeholder}</span>
                    </>
                )}
            </div>
        </div>
    );

    // ── Org mode: native file input ───────────────────────────────────────────

    if (orgId) {
        return (
            <>
                <NativeInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { void handleOrgFileChange(e); }}
                />
                <Button
                    aria-label={currentLogo ? 'Remove organization logo' : 'Select organization logo'}
                    className={`group relative block ${className}`}
                    onClick={() => { void handleOrgClick(); }}
                    size="none"
                    variant="plain"
                >
                    {logoContent}
                </Button>
            </>
        );
    }

    // ── Non-org editable mode: display only. Project production logos upload
    // through ProjectMediaUploader so repository files never become logo URLs.

    return (
        <div className={`group relative ${className}`}>
            {logoContent}
        </div>
    );
};
